/**
 * @fileoverview TipTap Font Size (Node Attribute) Extension
 *
 * Purpose: Mirrors the `fontSize` mark from `TextStyleKit` onto the block
 * nodes themselves so the bullet/number marker scales with the text.
 *
 * The default `FontSize` mark from TipTap only wraps text in
 * `<span style="font-size: …">`. The `<li>` marker glyph (the bullet or
 * number) inherits its size from the parent `<li>` element, not the inner
 * span, so changing font size via the toolbar only affects the text — the
 * marker stays at the default body size.
 *
 * This extension registers `fontSize` as a global attribute on
 * `paragraph`, `heading`, and `listItem` so we can also persist the size on
 * the block element. Combined with the FontSize mark from TextStyleKit,
 * this gives both the marker and the text a matching size.
 */

import { Extension } from "@tiptap/core";

export interface FontSizeNodeOptions {
  /** Block-level node types that should accept the `fontSize` attribute. */
  types: string[];
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    fontSizeNode: {
      /**
       * Apply a font-size value to the active block(s) (paragraph, heading,
       * listItem). Use this in conjunction with the FontSize mark so the
       * marker scales together with the text.
       */
      setNodeFontSize: (fontSize: string) => ReturnType;
      /** Clear the block-level font-size attribute. */
      unsetNodeFontSize: () => ReturnType;
    };
  }
}

export const FontSizeNode = Extension.create<FontSizeNodeOptions>({
  name: "fontSizeNode",

  addOptions() {
    return {
      types: ["paragraph", "heading", "listItem"],
    };
  },

  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          fontSize: {
            default: null,
            parseHTML: (element) => element.style.fontSize || null,
            renderHTML: (attributes) => {
              if (!attributes.fontSize) {
                return {};
              }
              return { style: `font-size: ${attributes.fontSize}` };
            },
          },
        },
      },
    ];
  },

  addCommands() {
    return {
      setNodeFontSize:
        (fontSize: string) =>
        ({ commands, editor }) => {
          // Only update the block types the cursor is actually inside.
          //
          // The naive `types.every(t => updateAttributes(t, …))` pattern is
          // broken here: when the selection is inside a `<li><p>text</p></li>`
          // and `paragraph, heading, listItem` is iterated, `paragraph`
          // succeeds, then `heading` returns false (no heading wraps the
          // selection), and `every()` short-circuits — so `listItem` never
          // gets its font-size attribute. The bullet glyph then stays at the
          // body's default size while the text scales up.
          // Run for every active type unconditionally (not `.some()`, which
          // would short-circuit and skip later types once one succeeds).
          const results = this.options.types
            .filter((type) => editor.isActive(type))
            .map((type) => commands.updateAttributes(type, { fontSize }));
          return results.some(Boolean);
        },
      unsetNodeFontSize:
        () =>
        ({ commands, editor }) => {
          const results = this.options.types
            .filter((type) => editor.isActive(type))
            .map((type) => commands.resetAttributes(type, "fontSize"));
          return results.some(Boolean);
        },
    };
  },
});
